/**
 * UI Utilities - Mobile-friendly UI components
 */

/**
 * Show a mobile-friendly confirmation dialog
 * @param {Object} options - Dialog options
 * @param {string} options.title - Dialog title
 * @param {string} options.message - Dialog message
 * @param {string} [options.confirmText='Confirm'] - Confirm button text
 * @param {string} [options.cancelText='Cancel'] - Cancel button text
 * @param {string} [options.type='default'] - Dialog type: 'default', 'danger', 'warning'
 * @returns {Promise<boolean>} - Resolves true if confirmed, false if cancelled
 */
export function showConfirm({ title, message, confirmText = 'Confirm', cancelText = 'Cancel', type = 'default' }) {
  return new Promise((resolve) => {
    // Create modal overlay
    const overlay = document.createElement('div')
    overlay.className = 'confirm-modal-overlay'

    const typeClass = type === 'danger' ? 'danger' : type === 'warning' ? 'warning' : ''

    overlay.innerHTML = `
      <div class="confirm-modal ${typeClass}">
        <div class="confirm-modal-content">
          <h3 class="confirm-modal-title">${title}</h3>
          <p class="confirm-modal-message">${message}</p>
        </div>
        <div class="confirm-modal-actions">
          <button class="confirm-modal-btn cancel">${cancelText}</button>
          <button class="confirm-modal-btn confirm ${typeClass}">${confirmText}</button>
        </div>
      </div>
    `

    document.body.appendChild(overlay)

    // Prevent body scroll
    document.body.style.overflow = 'hidden'

    const close = (result) => {
      document.body.style.overflow = ''
      overlay.classList.add('closing')
      setTimeout(() => {
        overlay.remove()
        resolve(result)
      }, 200)
    }

    // Handle button clicks
    overlay.querySelector('.confirm-modal-btn.cancel').addEventListener('click', () => close(false))
    overlay.querySelector('.confirm-modal-btn.confirm').addEventListener('click', () => close(true))

    // Handle overlay click (cancel)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close(false)
    })

    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', handleEscape)
        close(false)
      }
    }
    document.addEventListener('keydown', handleEscape)

    // Focus confirm button for accessibility
    setTimeout(() => {
      overlay.querySelector('.confirm-modal-btn.confirm').focus()
    }, 100)
  })
}

/**
 * Show a quick input prompt
 * @param {Object} options - Prompt options
 * @param {string} options.title - Dialog title
 * @param {string} options.placeholder - Input placeholder
 * @param {string} [options.defaultValue=''] - Default input value
 * @param {string} [options.inputType='text'] - Input type
 * @returns {Promise<string|null>} - Resolves with input value or null if cancelled
 */
export function showPrompt({ title, placeholder, defaultValue = '', inputType = 'text' }) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div')
    overlay.className = 'confirm-modal-overlay'

    overlay.innerHTML = `
      <div class="confirm-modal prompt-modal">
        <div class="confirm-modal-content">
          <h3 class="confirm-modal-title">${title}</h3>
          <input type="${inputType}" class="prompt-input" placeholder="${placeholder}" value="${defaultValue}" />
        </div>
        <div class="confirm-modal-actions">
          <button class="confirm-modal-btn cancel">Cancel</button>
          <button class="confirm-modal-btn confirm">OK</button>
        </div>
      </div>
    `

    document.body.appendChild(overlay)
    document.body.style.overflow = 'hidden'

    const input = overlay.querySelector('.prompt-input')

    const close = (value) => {
      document.body.style.overflow = ''
      overlay.classList.add('closing')
      setTimeout(() => {
        overlay.remove()
        resolve(value)
      }, 200)
    }

    overlay.querySelector('.confirm-modal-btn.cancel').addEventListener('click', () => close(null))
    overlay.querySelector('.confirm-modal-btn.confirm').addEventListener('click', () => close(input.value))

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') close(input.value)
      if (e.key === 'Escape') close(null)
    })

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close(null)
    })

    setTimeout(() => {
      input.focus()
      input.select()
    }, 100)
  })
}
